import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';

const VIEWER_URL_PARAMS = Object.freeze({
    isOpen: 'isSourceFileViewerOpen',
    reportId: 'sourceViewerReportId',
    fileId: 'sourceViewerFileId',
    fileName: 'sourceViewerFileName',
    fileType: 'sourceViewerFileType',
    fileSource: 'sourceViewerFileSource',
    folderId: 'sourceViewerFolderId',
    viewKey: 'sourceViewerViewKey',
});

const normalizeString = (value, fallback = null) => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    return String(value);
};

const createInitialState = () => ({
    isOpen: false,
    reportId: null,
    fileId: null,
    canonicalFileId: null,
    fileName: null,
    fileType: 'unknown',
    fileSource: null,
    sourceFolderId: null,
    sourceViewKey: null,
    // M6 federated search: optional sentence anchor to highlight / seek on open.
    sentenceHit: null,
    // Broadcast channel for "Edit classification" saves (document header). Any mounted
    // SourcesFileViewerContent watches this and patches its own fileDetails in place when the
    // update targets the file it currently has open, so an already-open viewer reflects the new
    // Classification Level/Distribution/Compartment immediately without a refetch or reopen.
    classificationUpdate: null,
    _initialized: false,
});

const updateUrlParams = (updates) => {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);

    Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, String(value));
        }
    });

    window.history.replaceState({}, '', url.toString());
};

const useSourcesFileViewerStore = create(
    subscribeWithSelector((set, get) => ({
        ...createInitialState(),

        open: ({
            reportId = null,
            fileId = null,
            canonicalFileId = null,
            fileName = null,
            fileType = 'unknown',
            fileSource = null,
            sourceFolderId = null,
            sourceViewKey = null,
            sentenceHit = null,
        } = {}) => {
            if (!fileId) {
                console.warn('[SourcesFileViewerStore] open() called without fileId');
                return;
            }

            set({
                isOpen: true,
                reportId: normalizeString(reportId),
                fileId: normalizeString(fileId),
                canonicalFileId: normalizeString(canonicalFileId),
                fileName: normalizeString(fileName),
                fileType: normalizeString(fileType, 'unknown'),
                fileSource: normalizeString(fileSource),
                sourceFolderId: normalizeString(sourceFolderId),
                sourceViewKey: normalizeString(sourceViewKey),
                sentenceHit: sentenceHit || null,
            });
        },

        setFile: ({
            reportId = null,
            fileId = null,
            canonicalFileId = null,
            fileName = null,
            fileType = 'unknown',
            fileSource = null,
            sourceFolderId = null,
            sourceViewKey = null,
            sentenceHit = null,
        } = {}) => {
            if (!fileId) {
                return;
            }

            set({
                isOpen: true,
                reportId: normalizeString(reportId, get().reportId),
                fileId: normalizeString(fileId),
                canonicalFileId: normalizeString(canonicalFileId),
                fileName: normalizeString(fileName),
                fileType: normalizeString(fileType, 'unknown'),
                fileSource: normalizeString(fileSource),
                sourceFolderId: normalizeString(sourceFolderId),
                sourceViewKey: normalizeString(sourceViewKey),
                sentenceHit: sentenceHit || null,
            });
        },

        // Called by SourcesEditClassificationDialog after a successful save. Not gated on
        // whether `fileId` is the currently-open file — any mounted viewer for that file (this
        // store-driven modal, or any other SourcesFileViewerContent instance watching the store)
        // picks it up via the subscription and patches its own state.
        notifyClassificationUpdated: ({fileId, classification} = {}) => {
            if (!fileId) {
                return;
            }
            set({classificationUpdate: {fileId: normalizeString(fileId), classification, ts: Date.now()}});
        },

        close: () => {
            set({
                isOpen: false,
                reportId: null,
                fileId: null,
                fileName: null,
                fileType: 'unknown',
                fileSource: null,
                sourceFolderId: null,
                sourceViewKey: null,
                sentenceHit: null,
            });
        },

        initFromUrl: (searchParams) => {
            if (get()._initialized) {
                return;
            }

            const isOpen = searchParams.get(VIEWER_URL_PARAMS.isOpen) === 'true';
            const fileId = normalizeString(searchParams.get(VIEWER_URL_PARAMS.fileId));

            if (isOpen && fileId) {
                set({
                    isOpen: true,
                    reportId: normalizeString(searchParams.get(VIEWER_URL_PARAMS.reportId)),
                    fileId,
                    fileName: normalizeString(searchParams.get(VIEWER_URL_PARAMS.fileName)),
                    fileType: normalizeString(searchParams.get(VIEWER_URL_PARAMS.fileType), 'unknown'),
                    fileSource: normalizeString(searchParams.get(VIEWER_URL_PARAMS.fileSource)),
                    sourceFolderId: normalizeString(searchParams.get(VIEWER_URL_PARAMS.folderId)),
                    sourceViewKey: normalizeString(searchParams.get(VIEWER_URL_PARAMS.viewKey)),
                    _initialized: true,
                });
                return;
            }

            set({_initialized: true});
        },

        reset: () => {
            set(createInitialState());
        },
    }))
);

useSourcesFileViewerStore.subscribe(
    (state) => ({
        isOpen: state.isOpen,
        reportId: state.reportId,
        fileId: state.fileId,
        fileName: state.fileName,
        fileType: state.fileType,
        fileSource: state.fileSource,
        sourceFolderId: state.sourceFolderId,
        sourceViewKey: state.sourceViewKey,
    }),
    ({isOpen, reportId, fileId, fileName, fileType, fileSource, sourceFolderId, sourceViewKey}) => {
        if (!useSourcesFileViewerStore.getState()._initialized) {
            return;
        }

        if (isOpen && fileId) {
            updateUrlParams({
                [VIEWER_URL_PARAMS.isOpen]: 'true',
                [VIEWER_URL_PARAMS.reportId]: reportId,
                [VIEWER_URL_PARAMS.fileId]: fileId,
                [VIEWER_URL_PARAMS.fileName]: fileName,
                [VIEWER_URL_PARAMS.fileType]: fileType,
                [VIEWER_URL_PARAMS.fileSource]: fileSource,
                [VIEWER_URL_PARAMS.folderId]: sourceFolderId,
                [VIEWER_URL_PARAMS.viewKey]: sourceViewKey,
            });
            return;
        }

        updateUrlParams({
            [VIEWER_URL_PARAMS.isOpen]: null,
            [VIEWER_URL_PARAMS.reportId]: null,
            [VIEWER_URL_PARAMS.fileId]: null,
            [VIEWER_URL_PARAMS.fileName]: null,
            [VIEWER_URL_PARAMS.fileType]: null,
            [VIEWER_URL_PARAMS.fileSource]: null,
            [VIEWER_URL_PARAMS.folderId]: null,
            [VIEWER_URL_PARAMS.viewKey]: null,
        });
    },
    {
        equalityFn: (a, b) => (
            a.isOpen === b.isOpen
            && a.reportId === b.reportId
            && a.fileId === b.fileId
            && a.fileName === b.fileName
            && a.fileType === b.fileType
            && a.fileSource === b.fileSource
            && a.sourceFolderId === b.sourceFolderId
            && a.sourceViewKey === b.sourceViewKey
        ),
    }
);

export default useSourcesFileViewerStore;

